'use client';

import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/TextArea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { HelpCircle, PencilLine, AlertCircle, Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { CreateEventData, Event } from '../../../services/EventService';
import { LimitedPlacesModal } from './LimitedPlacesModal';
import { ImageUpload } from './ImageUpload';
import { TimePicker } from '../../../components/ui/TimePicker';
import { supabase } from '../../../config/supabase';
import { processImageFile } from '../../../utils/imageProcessing';
import { CATEGORIES } from '../../../constants/categories';
import { URUGUAY_DEPARTMENTS } from '../../../constants/locations';

type EventFormMode = 'create' | 'edit';

interface FormErrors {
  name?: string;
  shortDescription?: string;
  startDate?: string;
  startTime?: string;
  category?: string;
  venue?: string;
  limitedPlaces?: string;
  general?: string;
}

interface EventFormProps {
  mode: EventFormMode;
  eventId?: string;
  onSubmit: (eventId: string) => void;
  onCancel: () => void;
}

const getDefaultFormData = () => ({
  name: '',
  shortDescription: '',
  description: '',
  websiteLink: '',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  timeZone: 'UTC',
  category: '',
  venue: '',
  coHosts: [] as string[],
  limitedPlaces: '',
  waitingList: true,
  requiresApproval: false,
  isPublic: 'Public' as 'Public' | 'Private',
  image: null as File | null,
  currentImageUrl: '',
  imageBlurPlaceholder: ''
});

const AVAILABLE_TIMEZONES: { value: string; label: string }[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/Argentina/Buenos_Aires', label: 'UTC-03:00' },
  { value: 'America/Sao_Paulo', label: 'UTC-03:00 / -02:00' },
  { value: 'America/New_York', label: 'UTC-05:00 / -04:00' },
  { value: 'America/Chicago', label: 'UTC-06:00 / -05:00' },
  { value: 'America/Denver', label: 'UTC-07:00 / -06:00' },
  { value: 'America/Los_Angeles', label: 'UTC-08:00 / -07:00' },
  { value: 'Europe/London', label: 'UTC+00:00 / +01:00' },
  { value: 'Europe/Madrid', label: 'UTC+01:00 / +02:00' },
  { value: 'Europe/Berlin', label: 'UTC+01:00 / +02:00' },
  { value: 'Europe/Paris', label: 'UTC+01:00 / +02:00' },
  { value: 'Europe/Zurich', label: 'UTC+01:00 / +02:00' },
  { value: 'Africa/Johannesburg', label: 'UTC+02:00' },
  { value: 'Asia/Dubai', label: 'UTC+04:00' },
  { value: 'Asia/Singapore', label: 'UTC+08:00' },
  { value: 'Asia/Tokyo', label: 'UTC+09:00' },
  { value: 'Australia/Sydney', label: 'UTC+10:00 / +11:00' }
];

const PUBLIC_OPTIONS: Array<'Public' | 'Private'> = ['Public', 'Private'];

export const EventForm: React.FC<EventFormProps> = ({ mode, eventId, onSubmit, onCancel }) => {
  const isEditMode = mode === 'edit';
  const { user, profile } = useAuth();

  const [formData, setFormData] = useState(getDefaultFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEditMode);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLimitedPlacesModalOpen, setIsLimitedPlacesModalOpen] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameSlugAvailable, setNameSlugAvailable] = useState<boolean | null>(null);
  const [lastCheckedName, setLastCheckedName] = useState('');
  const [originalSlug, setOriginalSlug] = useState('');

  const categories = CATEGORIES.map(category => category.name);
  const venueOptions = URUGUAY_DEPARTMENTS;

  useEffect(() => {
    if (!isEditMode) {
      setIsLoadingData(false);
      return;
    }

    if (!eventId) {
      setErrors(prev => ({
        ...prev,
        general: 'Event ID is missing. Please go back and try again.'
      }));
      setIsLoadingData(false);
      return;
    }

    const fetchEventData = async () => {
      try {
        setIsLoadingData(true);
        const { data: eventData, error } = await Event.getById(eventId);
        if (error || !eventData) {
          console.error('Error fetching event:', error);
          setErrors(prev => ({
            ...prev,
            general: 'We could not load the event information. Please try again later.'
          }));
          return;
        }

        const incomingTZ = eventData.time_zone;
        const normalizedTZ = incomingTZ && AVAILABLE_TIMEZONES.some(t => t.value === incomingTZ)
          ? incomingTZ
          : 'UTC';

        setFormData({
          name: eventData.title || '',
          shortDescription: eventData.short_description || '',
          description: eventData.description || '',
          websiteLink: eventData.website_link || '',
          startDate: eventData.start_date || '',
          startTime: eventData.start_time || '',
          endDate: eventData.end_date || '',
          endTime: eventData.end_time || '',
          timeZone: normalizedTZ,
          category: eventData.category || '',
          venue: eventData.location || '',
          coHosts: [],
          limitedPlaces: eventData.limited_places?.toString() || '',
          waitingList: eventData.waiting_list ?? true,
          requiresApproval: eventData.requires_approval ?? false,
          isPublic: eventData.is_public ? 'Public' : 'Private',
          image: null,
          currentImageUrl: eventData.image_url || '',
          imageBlurPlaceholder: ''
        });

        if (eventData.title) {
          setOriginalSlug(slugifyTitle(eventData.title));
        }
        setNameSlugAvailable(null);
        setLastCheckedName('');
      } catch (error) {
        console.error('Error loading event:', error);
        setErrors(prev => ({
          ...prev,
          general: 'We could not load the event information. Please try again later.'
        }));
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchEventData();
  }, [isEditMode, eventId]);

  useEffect(() => {
    const name = formData.name.trim();
    if (!name || name.length < 3) {
      setNameSlugAvailable(null);
      return;
    }
    if (name === lastCheckedName) return;

    const handle = setTimeout(async () => {
      setIsCheckingName(true);
      const available = await isSlugAvailable(name);
      setNameSlugAvailable(available);
      setLastCheckedName(name);
      setIsCheckingName(false);
      setErrors(prev => {
        const next = { ...prev };
        if (!available) {
          next.name = 'Another event with an equivalent name already exists';
        } else if (next.name && next.name.startsWith('Another event')) {
          delete next.name;
        }
        return next;
      });
    }, 500);

    return () => clearTimeout(handle);
  }, [formData.name, lastCheckedName]);

  const slugifyTitle = (title: string): string => {
    return (title || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/@/g, 'at')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  };

  const isSlugAvailable = async (name: string): Promise<boolean> => {
    const slug = slugifyTitle(name);
    if (!slug) return false;
    if (slug === originalSlug) return true;

    const parts = slug.split('-').filter(Boolean);
    const pattern = `%${parts.join('%')}%`;
    const { data, error } = await supabase
      .from('events')
      .select('id,title')
      .ilike('title', pattern)
      .limit(40);

    if (error) {
      console.warn('Slug availability check error:', error);
      return true;
    }

    const conflict = (data || []).some(e => e.id !== eventId && slugifyTitle(e.title) === slug);
    return !conflict;
  };

  const handleLimitedPlacesSave = (places: string, waitingList: boolean) => {
    setFormData(prev => ({
      ...prev,
      limitedPlaces: places,
      waitingList
    }));

    if (places && !isNaN(parseInt(places)) && parseInt(places) > 0) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.limitedPlaces;
        return newErrors;
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const fileName = `${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage.from('event-images').upload(fileName, file);

    if (error) {
      console.error('Error uploading image:', error);
      return null;
    }

    const { data: urlData } = supabase.storage.from('event-images').getPublicUrl(fileName);
    return urlData?.publicUrl || null;
  };

  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
  };

  const toLocalDateTime = (dateStr: string, timeStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
  };

  const isValidUrl = (value: string): boolean => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Event name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name cannot exceed 100 characters';
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short description is required';
    } else if (formData.shortDescription.length > 200) {
      newErrors.shortDescription = 'Short description cannot exceed 200 characters';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const startLocal = parseLocalDate(formData.startDate);
      const todayLocal = new Date();
      todayLocal.setHours(0, 0, 0, 0);
      if (startLocal < todayLocal) {
        newErrors.startDate = 'Date cannot be in the past';
      }
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.venue) {
      newErrors.venue = 'Venue is required';
    }

    if (formData.startDate) {
      const startDT = toLocalDateTime(formData.startDate, formData.startTime || '00:00');
      const endDT = toLocalDateTime(
        formData.endDate || formData.startDate,
        formData.endTime || '23:59'
      );
      if (endDT <= startDT) {
        newErrors.general = 'End date must be after start date';
      }
    }

    if (formData.limitedPlaces) {
      const places = parseInt(formData.limitedPlaces);
      if (isNaN(places) || places < 0) {
        newErrors.limitedPlaces = 'Number of places must be a valid number 0 or greater';
      } else if (places > 10000) {
        newErrors.limitedPlaces = 'Number of places cannot exceed 10,000';
      }
    }

    if (formData.websiteLink && !isValidUrl(formData.websiteLink)) {
      newErrors.general = 'Website link is not valid';
    }

    if (nameSlugAvailable === false) {
      newErrors.name = 'Another event with an equivalent name already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsCheckingName(true);
    const stillAvailable = await isSlugAvailable(formData.name.trim());
    setIsCheckingName(false);
    setNameSlugAvailable(stillAvailable);
    if (!stillAvailable) {
      setErrors(prev => ({ ...prev, name: 'Another event with an equivalent name already exists' }));
      return;
    }

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      let imageUrl: string | null = null;
      if (formData.image) {
        imageUrl = await uploadImage(formData.image);
      }

      if (isEditMode) {
        if (!eventId) {
          throw new Error('Missing event ID');
        }

        const updateData: Partial<CreateEventData> = {
          title: formData.name,
          short_description: formData.shortDescription,
          description: formData.description || undefined,
          website_link: formData.websiteLink || undefined,
          start_date: formData.startDate,
          start_time: formData.startTime,
          end_date: formData.endDate || undefined,
          end_time: formData.endTime || undefined,
          time_zone: formData.timeZone,
          location: formData.venue,
          location_detail: undefined,
          category: formData.category as CreateEventData['category'],
          limited_places: formData.limitedPlaces ? parseInt(formData.limitedPlaces) : undefined,
          waiting_list: formData.waitingList,
          requires_approval: formData.requiresApproval,
          is_public: formData.isPublic === 'Public',
          image_url: imageUrl || undefined
        };

        const { error } = await Event.update(eventId, updateData, user.id);
        if (error) {
          throw new Error(`Failed to update event: ${error.message}`);
        }

        onSubmit(eventId);
      } else {
        const createData: CreateEventData = {
          title: formData.name,
          short_description: formData.shortDescription,
          description: formData.description || undefined,
          website_link: formData.websiteLink || undefined,
          start_date: formData.startDate,
          start_time: formData.startTime,
          end_date: formData.endDate || undefined,
          end_time: formData.endTime || undefined,
          time_zone: formData.timeZone,
          location: formData.venue,
          location_detail: undefined,
          category: formData.category as CreateEventData['category'],
          limited_places: formData.limitedPlaces ? parseInt(formData.limitedPlaces) : undefined,
          waiting_list: formData.waitingList,
          requires_approval: formData.requiresApproval,
          is_public: formData.isPublic === 'Public',
          image_url: imageUrl || undefined
        };

        const profilePayload = {
          name: profile?.full_name || user.user_metadata?.full_name,
          email: profile?.email || user.email || undefined,
          avatarUrl: profile?.avatar_url || (user.user_metadata?.avatar_url as string) || ''
        };

        const { data, error } = await Event.create(createData, user.id, profilePayload);
        if (error || !data) {
          throw new Error(error?.message || 'Failed to create event');
        }

        onSubmit(data.id);
      }
    } catch (error) {
      console.error('Error saving event:', error);
      setErrors({ general: error instanceof Error ? error.message : 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  if (isEditMode && !eventId) {
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        <Card className="p-4 md:p-8">
          <div className="flex items-center justify-center h-32">
            <div className="text-lg text-red-600">Missing event ID. Please return to the dashboard.</div>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        <Card className="p-4 md:p-8">
          <div className="flex items-center justify-center h-32">
            <div className="text-lg">Loading event data...</div>
          </div>
        </Card>
      </div>
    );
  }

  const submitLabel = isEditMode ? 'Update event' : 'Create event';
  const loadingLabel = isEditMode ? 'Updating event...' : 'Creating event...';

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <Card className="p-4 md:p-8">
        <form onSubmit={handleSubmit}>
          {errors.general && (
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{errors.general}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
            <div className="w-full h-40 sm:h-48 md:h-56 bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors relative overflow-hidden">
              <ImageUpload
                onImageSelect={async (file) => {
                  if (!file) {
                    handleInputChange('image', null);
                    return;
                  }
                  try {
                    const { file: processedFile, placeholderBlurDataUrl } = await processImageFile(file, {
                      aspectRatio: 16 / 9,
                      fit: 'smart',
                      smartCropTolerance: 0.18,
                      targetWidth: 1600,
                      upscale: true,
                      background: 'blur',
                      outputType: 'image/webp',
                      quality: 0.82
                    });
                    handleInputChange('image', processedFile);
                    if (placeholderBlurDataUrl) {
                      handleInputChange('imageBlurPlaceholder', placeholderBlurDataUrl);
                    }
                  } catch (err) {
                    console.error('Image processing failed:', err);
                  }
                }}
                currentImage={formData.image}
                currentImageUrl={formData.currentImageUrl}
              />
            </div>

            <div className="w-full space-y-4 md:space-y-6">
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">
                  Event name<span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Event name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`mb-3 ${errors.name ? 'border-red-500' : ''}`}
                />
                {formData.name.trim().length >= 3 && !errors.name && (
                  <p className="text-xs text-gray-500 mb-2">
                    {isCheckingName
                      ? 'Checking name availability...'
                      : nameSlugAvailable === false
                        ? 'This name (slug) is already taken.'
                        : nameSlugAvailable === true
                          ? 'Name is available.'
                          : ''}
                  </p>
                )}

                {errors.name && (
                  <p className="text-red-500 text-sm mb-3">{errors.name}</p>
                )}

                <Textarea
                  placeholder="Short event description *"
                  value={formData.shortDescription}
                  onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                  rows={2}
                  className={`mb-2 md:mb-3 ${errors.shortDescription ? 'border-red-500' : ''}`}
                />
                {errors.shortDescription && (
                  <p className="text-red-500 text-sm mb-2 md:mb-3">{errors.shortDescription}</p>
                )}

                <Textarea
                  placeholder="Full event description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="mb-2 md:mb-3"
                />

                <Input
                  placeholder="Website link"
                  value={formData.websiteLink}
                  onChange={(e) => handleInputChange('websiteLink', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <Input
                    type="date"
                    placeholder="Start date *"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className={errors.startDate ? 'border-red-500' : ''}
                  />
                  {errors.startDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
                  )}
                </div>
                <div className="w-full">
                  <TimePicker
                    value={formData.startTime}
                    onChange={(val) => handleInputChange('startTime', val)}
                    error={!!errors.startTime}
                  />
                  {errors.startTime && (
                    <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>
                  )}
                </div>
                <div>
                  <Input
                    type="date"
                    placeholder="End date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                  />
                </div>
                <div className="w-full">
                  <TimePicker
                    value={formData.endTime}
                    onChange={(val) => handleInputChange('endTime', val)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:gap-4">
                <Select
                  value={formData.timeZone}
                  onValueChange={(value) => handleInputChange('timeZone', value)}
                >
                  <SelectTrigger className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                    <SelectValue placeholder="Time zone" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.value === 'UTC'
                          ? tz.label
                          : `${tz.label} ${tz.value.replace(/_/g, ' ').replace(/\//g, ' / ')}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 md:mb-6">Event options</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Category *" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              <div>
                <Select value={formData.venue} onValueChange={(value) => handleInputChange('venue', value)}>
                  <SelectTrigger className={errors.venue ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Provincia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Sin especificar</SelectItem>
                    {venueOptions.map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.venue && (
                  <p className="text-red-500 text-sm mt-1">{errors.venue}</p>
                )}
              </div>
            </div>

            <div className="w-full mb-4 md:mb-6">
              <div className="space-y-2">
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsLimitedPlacesModalOpen(true)}
                    className="w-full justify-start text-gray-500 border-gray-200 pr-10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Limited places
                  </Button>
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <PencilLine className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                {errors.limitedPlaces && (
                  <p className="text-red-500 text-sm mt-1">{errors.limitedPlaces}</p>
                )}
              </div>
            </div>

            <LimitedPlacesModal
              isOpen={isLimitedPlacesModalOpen}
              onClose={() => setIsLimitedPlacesModalOpen(false)}
              onSave={handleLimitedPlacesSave}
              initialPlaces={formData.limitedPlaces}
              initialWaitingList={formData.waitingList}
            />

            <div className="space-y-4 md:space-y-6 mb-4 md:mb-6">
              <div className="flex w-full">
                <div className="flex w-full items-center justify-between py-2 px-3 md:px-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-700 font-medium">Requires approval</span>
                  </div>
                  <Switch
                    checked={formData.requiresApproval}
                    onCheckedChange={(checked) => handleInputChange('requiresApproval', checked)}
                  />
                </div>
              </div>
              <div className="mt-1 mb-3 md:mb-4 text-sm text-gray-500">
                If approval is required, guests must request to attend and you’ll need to approve them manually.
              </div>

              <div className='flex w-full'>
                <div className='w-full flex items-start'>
                  <div className="flex-1 min-w-0">
                    <Select value={formData.isPublic} onValueChange={(value) => handleInputChange('isPublic', value)}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PUBLIC_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative group ml-1 shrink-0">
                    <button
                      type="button"
                      className="border border-purple-300 text-purple-600 rounded p-1 cursor-help focus:outline-none focus:ring-2 focus:ring-purple-500"
                      aria-label="Visibility information"
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                    <div
                      className="pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity
                                 absolute z-20 left-1/2 -translate-x-1/2 top-full mt-2 w-60 rounded-md bg-primary text-white
                                 text-xs leading-relaxed px-3 py-2 shadow-lg"
                      role="tooltip"
                    >
                      {formData.isPublic === 'Public'
                        ? 'Public: Event is visible to everyone.'
                        : 'Private: Only invited guests can view and attend.'}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-primary rotate-45" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 md:gap-4 mt-6 md:mt-8">
            <Button
              type="submit"
              className="flex-1 bg-accent text-white hover:bg-accent/90"
              disabled={isLoading}
            >
              {isLoading ? loadingLabel : submitLabel}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

